import React from 'react'
import { useTranslation } from 'react-i18next'
import { Trophy, Check, ChevronRight, Circle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { useUnits } from '../../../contexts/UnitsContext'

export function MilestonesCard({ milestones, currentWeight }) {
  const { t } = useTranslation()
  const { weightUnit, formatWeight } = useUnits()

  if (!milestones || milestones.length === 0) return null

  // First unreached index = next target. -1 if all reached.
  const nextIndex = milestones.findIndex((m) => m.reachedDate == null)
  const allReached = nextIndex === -1

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="w-4 h-4 text-app-primary-500" />
          {t('phoenixTracker.progress.milestones.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {allReached && (
          <p className="text-sm text-green-500 mb-3 font-medium">
            🎉 {t('phoenixTracker.progress.milestones.allReached')}
          </p>
        )}
        <div className="space-y-1.5">
          {milestones.map((m, i) => {
            const reached = m.reachedDate != null
            const isNext = i === nextIndex
            const remaining = currentWeight != null && !reached
              ? Math.max(0, currentWeight - m.weight)
              : null

            return (
              <div
                key={m.weight}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                  isNext && 'bg-app-primary-50 dark:bg-app-primary-900/20 border border-app-primary-500/40',
                  !isNext && reached && 'bg-accent/30',
                  !isNext && !reached && 'opacity-60'
                )}
              >
                {/* Icon */}
                <div
                  className={cn(
                    'shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                    reached && 'bg-green-500/20 text-green-500',
                    isNext && 'bg-app-primary-500 text-white',
                    !reached && !isNext && 'border border-muted-foreground/30 text-muted-foreground'
                  )}
                >
                  {reached ? (
                    <Check className="w-4 h-4" />
                  ) : isNext ? (
                    <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
                  ) : (
                    <Circle className="w-2 h-2" />
                  )}
                </div>

                {/* Weight */}
                <div className="flex-1 flex items-center gap-2">
                  <span
                    className={cn(
                      'font-mono font-semibold',
                      isNext && 'text-app-primary-500',
                      reached && !isNext && 'text-foreground',
                      !reached && !isNext && 'text-muted-foreground'
                    )}
                  >
                    {formatWeight(m.weight)} <span className="text-xs font-normal text-muted-foreground ms-1">{weightUnit}</span>
                  </span>
                  {m.isGoal && (
                    <span className="text-[10px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500">
                      {t('phoenixTracker.progress.milestones.goal')}
                    </span>
                  )}
                </div>

                {/* Right-side text */}
                <div className="text-xs text-end">
                  {reached ? (
                    <span className="text-muted-foreground font-mono">
                      {t('phoenixTracker.progress.milestones.reached', {
                        date: new Date(m.reachedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      })}
                    </span>
                  ) : isNext && remaining != null ? (
                    <span className="text-app-primary-500 font-mono font-medium">
                      {t('phoenixTracker.progress.milestones.toGo', { value: formatWeight(remaining), unit: weightUnit })}
                    </span>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
